import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSalonBrand } from './useSalonBrand'
import { ApplyBrandToLocations } from './ApplyBrandToLocations'
import { BrandPanel } from '../email-composer/components/BrandPanel'
import { ImagePanel } from '../email-composer/components/ImagePanel'
import { SmoothEmailFrame } from './SmoothEmailFrame'
import { resolveTheme } from '../email-composer/email/theme'
import { previewHtml } from './brandPreview'
import BrandStudio from './BrandStudio'
import { hapticSuccess, hapticTick } from '../../../../_utils/haptics'
import '../email-composer/EmailBuilder.css'
import './BrandCard.css'
import './BrandPage.css'

// Marketing › Config › Your brand: the salon's brand on a page of its own.
// The controls run down the left; the preview is held still on the right, so
// every change shows where it lands (on a phone it rides along at the top).
// The brand saves as it changes, so the way out is Done.

const IcBack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
)
const IcTick = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.2 4.2L19 7" /></svg>
)

const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 767)
  useEffect(() => {
    const on = () => setPhone(window.innerWidth <= 767)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return phone
}

export const BrandPage = ({ onDone }) => {
  const brand = useSalonBrand({ syncSalon: true })
  const [pickingLogo, setPickingLogo] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const phone = usePhone()
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])
  // The app shell clips sideways overflow in a way that stops anything
  // sticking (see BrandPage.css); while this page is open it clips without
  // that, so the preview holds still
  useEffect(() => {
    document.body.classList.add('brp-on')
    return () => document.body.classList.remove('brp-on')
  }, [])
  const theme = resolveTheme(brand.spec)
  const html = useMemo(() => previewHtml(brand.spec), [brand.spec])
  // "Saved" for a moment after each change; otherwise what's true all along
  const status = brand.saved ? 'Saved' : brand.changes ? 'All changes saved' : 'Changes save as you go'

  // Whatever is still waiting to save goes first, then back to Config
  const done = async () => {
    if (leaving) return
    hapticTick()
    setLeaving(true)
    try { await brand.flush() } catch (e) { /* the brand is kept on this device and saved next time */ }
    if (!alive.current) return
    hapticSuccess()
    onDone()
  }

  // Phones get the studio: the email full screen, tools along the bottom
  if (phone) return <BrandStudio brand={brand} onDone={done} leaving={leaving} />

  return (
    <div className="brp">
      <div className="brp-layout">
        <div className="brp-main">
          <header className="brp-intro">
            <h1 className="brp-title">Your brand</h1>
            <p className="brp-sub">Your logo, colour and type, set once. Every new email starts with them, and your booking emails wear them too.</p>
          </header>

          <div className="brp-panel">
            {pickingLogo ? (
              <div className="brc-picker">
                <button type="button" className="brc-back" onClick={() => { hapticTick(); setPickingLogo(false) }}><IcBack /> Choose your logo</button>
                <ImagePanel
                  salon={brand.salon}
                  onSelect={(url) => {
                    brand.update({ ...brand.spec, logo: url, logoShape: undefined, logoShapeFor: undefined })
                    setPickingLogo(false)
                  }}
                />
              </div>
            ) : (
              <BrandPanel
                spec={brand.spec}
                salonKit={brand.salonKit}
                onChange={brand.update}
                onPickLogo={() => setPickingLogo(true)}
                kitSaved={brand.saved}
              />
            )}
          </div>
          {brand.syncNote && <p className="brc-note brp-note">{brand.syncNote}</p>}
          <ApplyBrandToLocations changes={brand.changes} flush={brand.flush} />
        </div>

        <aside className="brp-side" aria-label="Preview">
          <div className="brp-preview">
            <div className="brp-preview-head">
              <b>How your emails open</b>
              <span className={`brp-status${brand.saved ? ' is-saved' : ''}`} role="status" aria-live="polite">
                {brand.saved && <IcTick />}{status}
              </span>
            </div>
            <div className="brp-stage" style={{ background: theme.palette.canvas }}>
              {brand.loading ? <div className="brp-hold" /> : <SmoothEmailFrame html={html} width={600} title="Your brand in an email" />}
            </div>
          </div>
          <div className="brp-done">
            <button type="button" className="brp-done-btn" onClick={done} disabled={leaving}>{leaving ? 'Saving…' : 'Done'}</button>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default BrandPage
